/**
 * ManipulativeEmbed.jsx — Cycle 55 · Teach & Explain
 * ───────────────────────────────────────────────────
 * Inline renderer for lesson pages. Accepts a `slug` (matches a
 * tool route) and a `config` string (URL query string) and renders
 * the corresponding tool's Display component in read-only-ish mode.
 * A small "Open full tool" chip links to the fully-interactive
 * version at /tools/{slug}?{config}.
 *
 * Slugs are stable — the lesson generator emits them into the
 * `section.manipulative` field.
 */
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Sparkles } from "lucide-react";

import { BaseTenDisplay }     from "@/pages/tools/BaseTenTool";
import { BeadStringDisplay }  from "@/pages/tools/BeadStringTool";
import { CountersDisplay }    from "@/pages/tools/CountersTool";
import { ArrayDisplay }       from "@/pages/tools/ArrayTool";
import { TenFrameDisplay }    from "@/pages/tools/TenFrameTool";
import { BarModelDisplay }    from "@/pages/tools/BarModelTool";
import { ClockDisplay }       from "@/pages/tools/ClockTool";

const clampInt = (v, lo, hi, def) => {
  const n = parseInt(v ?? "", 10);
  if (isNaN(n)) return def;
  return Math.max(lo, Math.min(hi, n));
};

// Each entry: parses `params` (URLSearchParams) and renders. Mutations
// are optional — tools that don't opt in show read-only preview mode.
const REGISTRY = {
  "base-ten": {
    title: "Base-Ten Blocks",
    parse: (p) => ({
      u: clampInt(p.get("u"), 0, 200, 0),
      r: clampInt(p.get("r"), 0, 200, 0),
      f: clampInt(p.get("f"), 0, 200, 0),
      c: clampInt(p.get("c"), 0, 200, 0),
    }),
    render: (state, setState) => (
      <BaseTenDisplay counts={state} setCounts={setState} />
    ),
  },
  "bead-string": {
    title: "Bead String",
    parse: (p) => {
      const rows = clampInt(p.get("rows"), 1, 4, 2);
      const perRow = clampInt(p.get("per"), 5, 20, 10);
      const active = (p.get("act") || "0,0").split(",").map((n) => clampInt(n, 0, perRow, 0));
      while (active.length < rows) active.push(0);
      active.length = rows;
      return { rows, perRow, active };
    },
    render: (state, setState) => (
      <BeadStringDisplay rows={state.rows} perRow={state.perRow} active={state.active}
                         setActive={(next) => setState({ ...state, active: next })} />
    ),
  },
  "counters": {
    title: "Two-Colour Counters",
    parse: (p) => {
      const n = clampInt(p.get("n"), 2, 60, 20);
      const seq = (p.get("s") || "").toUpperCase().replace(/[^RY]/g, "");
      const counters = Array.from({ length: n }, (_, i) => (seq[i] === "R" || seq[i] === "Y") ? seq[i] : "Y");
      return { counters };
    },
    render: (state, setState) => (
      <CountersDisplay counters={state.counters}
                       setCounters={(next) => setState({ counters: next })} />
    ),
  },
  "arrays": {
    title: "Arrays",
    parse: (p) => {
      const rows = clampInt(p.get("r"), 1, 12, 4);
      const cols = clampInt(p.get("c"), 1, 12, 6);
      return {
        rows, cols,
        sx: clampInt(p.get("sx"), 0, cols, 0),
        sy: clampInt(p.get("sy"), 0, rows, 0),
        showPartials: p.get("partials") !== "0",
      };
    },
    render: (state) => <ArrayDisplay {...state} />,
  },
  "ten-frame": {
    title: "Ten Frame",
    parse: (p) => {
      const frames = clampInt(p.get("frames"), 1, 2, 1);
      const filled = (p.get("filled") || "0:0").split(":").map((n) => clampInt(n, 0, 10, 0));
      while (filled.length < frames) filled.push(0);
      filled.length = frames;
      return { frames, filled };
    },
    render: (state, setState) => (
      <TenFrameDisplay frames={state.frames} filled={state.filled}
                       setFilled={(next) => setState({ ...state, filled: next })} />
    ),
  },
  "bar-model": {
    title: "Bar Model",
    parse: (p) => {
      const mode = (p.get("mode") || "compare") === "part" ? "part" : "compare";
      return {
        mode,
        a: parseFloat(p.get("a") || 8) || 0,
        b: parseFloat(p.get("b") || 5) || 0,
        la: p.get("la") || "",
        lb: p.get("lb") || "",
        parts: (p.get("parts") || "3,4,5").split(",").map((v) => parseFloat(v) || 0),
        labels: (p.get("labels") || "").split(","),
        totalLabel: p.get("total") || "",
      };
    },
    render: (state) => <BarModelDisplay config={state} />,
  },
  "clock": {
    title: "Clock",
    parse: (p) => ({
      h: clampInt(p.get("h"), 1, 12, 3),
      m: clampInt(p.get("m"), 0, 59, 0),
      digital: p.get("digital") !== "0",
    }),
    render: (state, setState) => (
      <ClockDisplay h={state.h} m={state.m} digital={state.digital}
                    setTime={({ h, m }) => setState({ ...state, h, m })} />
    ),
  },
  "number-line": {
    // Number Line + Place Value + Fraction Wall aren't rewired for
    // prop-driven display yet; fall through to iframe fallback below.
    fallback: true, title: "Number Line",
  },
  "place-value-chart": { fallback: true, title: "Place Value Chart" },
  "fraction-wall":     { fallback: true, title: "Fraction Wall" },
};

export default function ManipulativeEmbed({ slug, config, height = 420 }) {
  const entry = REGISTRY[slug];
  const params = useMemo(() => new URLSearchParams(config || ""), [config]);
  const [state, setState] = useState(() => entry?.parse ? entry.parse(params) : null);

  if (!entry) {
    return (
      <div className="mt-4 rounded-2xl border-[3px] border-dashed border-slate-400 bg-slate-50 p-3 text-xs font-bold text-slate-500" data-testid="me-unknown">
        Unknown manipulative: <code>{slug}</code>
      </div>
    );
  }

  const openUrl = `/tools/${slug}${config ? `?${config}` : ""}`;

  if (entry.fallback) {
    return (
      <FallbackEmbed slug={slug} title={entry.title} openUrl={openUrl} height={height} />
    );
  }

  return (
    <div className="mt-5 rounded-3xl border-[3px] border-slate-900 bg-gradient-to-br from-sky-50 via-white to-emerald-50 p-3 md:p-4 shadow-[4px_4px_0_0_rgba(15,23,42,1)]"
         data-testid={`me-embed-${slug}`}>
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <p className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-fuchsia-700">
          <Sparkles size={12} strokeWidth={3} /> {entry.title} · try it
        </p>
        <Link to={openUrl} target="_blank" rel="noreferrer" data-testid={`me-open-${slug}`}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-full border-2 border-slate-900 bg-white font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)]">
          Open full tool <ExternalLink size={12} strokeWidth={3} />
        </Link>
      </div>
      {entry.render(state, setState)}
    </div>
  );
}

function FallbackEmbed({ slug, title, openUrl, height }) {
  return (
    <div className="mt-5 rounded-3xl border-[3px] border-slate-900 bg-white shadow-[4px_4px_0_0_rgba(15,23,42,1)] overflow-hidden"
         data-testid={`me-embed-${slug}`}>
      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b-[3px] border-slate-900 gap-2 flex-wrap">
        <p className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-fuchsia-700">
          <Sparkles size={12} strokeWidth={3} /> {title}
        </p>
        <Link to={openUrl} target="_blank" rel="noreferrer" data-testid={`me-open-${slug}`}
              className="inline-flex items-center gap-1 h-8 px-3 rounded-full border-2 border-slate-900 bg-white font-bold text-xs shadow-[0_2px_0_0_rgba(15,23,42,1)]">
          Open full tool <ExternalLink size={12} strokeWidth={3} />
        </Link>
      </div>
      <iframe src={openUrl} title={title} className="w-full" style={{ height }} data-testid={`me-iframe-${slug}`} />
    </div>
  );
}
